// Funções helper para diagnóstico e correção de OSs
// Para usar no console do navegador, importe e execute as funções

import { supabase } from '@/integrations/supabase/client';
import { calcularValorFaturamentoOS, getValorHoraClienteVigente } from './ordensServicoValueQueries';
import { clearClienteCache } from './ordensServicoCache';
import { apiCall } from '@/utils/ordensServicoApi';
import { buildQueryParams } from '@/utils/ordensServicoQueryParams';
import { convertHoursToDecimal } from './timeConversion';

// Funções globais para uso direto no console
declare global {
  interface Window {
    diagnosticarOS: (numeroOS: string) => Promise<void>;
    diagnosticarOSs: (numerosOS: string[]) => Promise<void>;
    corrigirOS: (numeroOS: string) => Promise<void>;
    corrigirOSs: () => Promise<void>;
    verificarCliente: (nomeCliente: string) => Promise<void>;
    listarClientesSemValor: () => Promise<void>;
    diagnosticarSincronizacao: (dataInicio?: string, dataFim?: string) => Promise<void>;
    limparCache: () => void;
  }
}

// Função para diagnosticar uma OS específica
export const diagnosticarOS = async (numeroOS: string) => {
  console.log(`🔍 === DIAGNÓSTICO DA OS ${numeroOS} ===`);
  
  try {
    // 1. Buscar a OS
    const { data: ordem, error: ordemError } = await supabase
      .from('ordens_servico')
      .select('*')
      .eq('numero', numeroOS)
      .maybeSingle();

    if (ordemError || !ordem) {
      console.error(`❌ OS ${numeroOS} não encontrada`);
      return;
    }

    console.log(`✅ OS encontrada: ${ordem.numero} - ${ordem.empresa_nome}`);
    console.log(`📊 Dados da OS:`);
    console.log(`  - Cliente: ${ordem.empresa_nome} (ID: ${ordem.cliente_cadastro_id})`);
    console.log(`  - Data: ${ordem.data_real}`);
    console.log(`  - Horas faturadas: ${ordem.horas_faturadas}`);
    console.log(`  - Valor faturamento: R$ ${ordem.valor_faturamento}`);

    // 2. Verificar se tem cliente_cadastro_id
    if (!ordem.cliente_cadastro_id) {
      console.warn(`⚠️ ATENÇÃO: OS ${numeroOS} não possui cliente_cadastro_id!`);
      console.warn(`   Isso pode causar problemas na busca do valor/hora.`);
    }

    // 3. Buscar cliente
    if (ordem.cliente_cadastro_id) {
      const { data: cliente, error: clienteError } = await supabase
        .from('clientes_cadastro')
        .select('*')
        .eq('id', ordem.cliente_cadastro_id)
        .maybeSingle();

      if (clienteError || !cliente) {
        console.error(`❌ Cliente ID ${ordem.cliente_cadastro_id} não encontrado`);
      } else {
        console.log(`✅ Cliente encontrado: ${cliente.nome} (ID: ${cliente.id})`);
        
        // 4. Buscar valores históricos do cliente
        const { data: valoresHistorico, error: valoresError } = await supabase
          .from('clientes_valores_historico')
          .select('*')
          .eq('cliente_id', cliente.id)
          .order('id', { ascending: false });

        if (valoresError) {
          console.error(`❌ Erro ao buscar valores históricos:`, valoresError);
        } else {
          console.log(`📊 Valores históricos do cliente ${cliente.nome}:`);
          if (valoresHistorico && valoresHistorico.length > 0) {
            valoresHistorico.forEach((valor, index) => {
              console.log(`  ${index + 1}. ID: ${valor.id}, Data: ${valor.data_reajuste}, Valor: R$ ${valor.valor_hora}`);
            });
            
            const valorMaisRecente = valoresHistorico[0];
            console.log(`\n💰 Valor mais recente: R$ ${valorMaisRecente.valor_hora} (ID: ${valorMaisRecente.id}, Data: ${valorMaisRecente.data_reajuste})`);
          } else {
            console.warn(`⚠️ Cliente ${cliente.nome} não possui valores históricos`);
          }
        }
      }
    }

    // 5. Testar cálculo do valor/hora vigente
    console.log(`\n🧮 TESTANDO CÁLCULO DO VALOR/HORA VIGENTE...`);
    try {
      const valorHoraVigente = await getValorHoraClienteVigente(ordem.cliente_cadastro_id, ordem.empresa_nome, ordem.data_real);
      
    } catch (error) {
      console.error(`❌ Erro ao calcular valor/hora vigente:`, error);
    }

    // 6. Testar cálculo do valor de faturamento
    console.log(`\n💰 TESTANDO CÁLCULO DO VALOR DE FATURAMENTO...`);
    try {
      const valorFaturamento = await calcularValorFaturamentoOS(ordem.numero);
      console.log(`✅ Valor de faturamento calculado: R$ ${valorFaturamento}`);
      
      if (ordem.valor_faturamento !== valorFaturamento) {
        console.warn(`⚠️ VALOR INCONSISTENTE!`);
        console.warn(`   Valor atual: R$ ${ordem.valor_faturamento}`);
        console.warn(`   Valor calculado: R$ ${valorFaturamento}`);
      } else {
        console.log(`✅ Valores consistentes!`);
      }
    } catch (error) {
      console.error(`❌ Erro ao calcular valor de faturamento:`, error);
    }

    // 4. Testar a função getValorHoraClienteVigente
    if (ordem.empresa_nome && ordem.data_real) {
      console.log(`🧮 Testando getValorHoraClienteVigente para "${ordem.empresa_nome}" na data ${ordem.data_real}:`);
      try {
        const valorHora = await getValorHoraClienteVigente(null, ordem.empresa_nome, ordem.data_real);
        console.log(`✅ Valor/hora encontrado: R$ ${valorHora}/hora`);
        
        // Converter horas para decimal para cálculo correto
        const horasFaturadas = convertHoursToDecimal(ordem.horas_faturadas || '0');
        console.log(`⏰ Horas faturadas convertidas: "${ordem.horas_faturadas}" → ${horasFaturadas.toFixed(2)}h`);
        
        const faturamentoCalculado = horasFaturadas * valorHora;
        console.log(`🧮 Faturamento calculado: ${horasFaturadas.toFixed(2)}h × R$ ${valorHora}/h = R$ ${faturamentoCalculado.toFixed(2)}`);
        
        if (ordem.valor_faturamento !== faturamentoCalculado) {
          console.warn(`⚠️ VALOR INCONSISTENTE!`);
          console.warn(`   Valor atual: R$ ${ordem.valor_faturamento}`);
          console.warn(`   Valor calculado: R$ ${faturamentoCalculado}`);
        } else {
          console.log(`✅ Valores consistentes!`);
        }
      } catch (error) {
        console.error(`❌ Erro ao testar getValorHoraClienteVigente:`, error);
      }
    }

  } catch (error) {
    console.error(`❌ Erro no diagnóstico da OS ${numeroOS}:`, error);
  }
};

// Função para diagnosticar múltiplas OSs
export const diagnosticarOSs = async (numerosOS: string[]) => {
  console.log(`🔍 === DIAGNÓSTICO DE MÚLTIPLAS OSs ===`);
  
  for (const numeroOS of numerosOS) {
    console.log(`\n${'='.repeat(50)}`);
    await diagnosticarOS(numeroOS);
  }
};

// Função para corrigir uma OS específica
export const corrigirOS = async (numeroOS: string) => {
  console.log(`🔧 === CORRIGINDO OS ${numeroOS} ===`);
  
  try {
    // 1. Buscar a OS
    const { data: ordem, error: ordemError } = await supabase
      .from('ordens_servico')
      .select('*')
      .eq('numero', numeroOS)
      .maybeSingle();

    if (ordemError || !ordem) {
      console.error(`❌ OS ${numeroOS} não encontrada`);
      return;
    }

    console.log(`✅ OS encontrada: ${ordem.numero} - ${ordem.empresa_nome}`);

    // 2. Verificar se precisa de correção
    if (!ordem.cliente_cadastro_id) {
      console.warn(`⚠️ OS ${numeroOS} não possui cliente_cadastro_id`);
      
      // Buscar cliente pelo nome
      const { data: cliente, error: clienteError } = await supabase
        .from('clientes_cadastro')
        .select('*')
        .eq('nome', ordem.empresa_nome)
        .maybeSingle();

      if (clienteError || !cliente) {
        console.error(`❌ Cliente "${ordem.empresa_nome}" não encontrado`);
        return;
      }

      console.log(`✅ Cliente encontrado: ${cliente.nome} (ID: ${cliente.id})`);

      // 3. Atualizar a OS com o cliente_cadastro_id
      const { error: updateError } = await supabase
        .from('ordens_servico')
        .update({ cliente_cadastro_id: cliente.id })
        .eq('numero', numeroOS);

      if (updateError) {
        console.error(`❌ Erro ao atualizar OS:`, updateError);
        return;
      }

      console.log(`✅ OS ${numeroOS} atualizada com cliente_cadastro_id: ${cliente.id}`);
    } else {
      console.log(`✅ OS ${numeroOS} já possui cliente_cadastro_id: ${ordem.cliente_cadastro_id}`);
    }

    // 4. Recalcular valor de faturamento
    console.log(`\n💰 RECALCULANDO VALOR DE FATURAMENTO...`);
    try {
      const valorFaturamento = await calcularValorFaturamentoOS(numeroOS);
      console.log(`✅ Valor calculado: R$ ${valorFaturamento}`);

      // Atualizar o valor na OS
      const { error: valorError } = await supabase
        .from('ordens_servico')
        .update({ valor_faturamento: valorFaturamento })
        .eq('numero', numeroOS);

      if (valorError) {
        console.error(`❌ Erro ao atualizar valor:`, valorError);
      } else {
        console.log(`✅ Valor de faturamento atualizado na OS ${numeroOS}`);
      }
    } catch (error) {
      console.error(`❌ Erro ao recalcular valor:`, error);
    }

  } catch (error) {
    console.error(`❌ Erro ao corrigir OS ${numeroOS}:`, error);
  }
};

// Função para corrigir múltiplas OSs
export const corrigirOSsProblematicas = async () => {
  console.log(`🔧 === CORRIGINDO OSs PROBLEMÁTICAS ===`);
  
  const osProblematicas = ['24983', '25016'];
  
  for (const numeroOS of osProblematicas) {
    console.log(`\n${'='.repeat(50)}`);
    await corrigirOS(numeroOS);
  }
};

// Função para diagnosticar sincronização
export const diagnosticarSincronizacao = async (dataInicio?: string, dataFim?: string) => {
  console.log(`🔍 === DIAGNÓSTICO DE SINCRONIZAÇÃO ===`);
  
  try {
    // Usar datas padrão se não fornecidas
    const inicio = dataInicio || '2025-01-01';
    const fim = dataFim || '2025-01-31';
    
    console.log(`📅 Período: ${inicio} a ${fim}`);
    
    // 1. Testar parâmetros da query
    const queryParams = buildQueryParams({ dataInicio: inicio, dataFim: fim });
    console.log(`🔗 Parâmetros da query:`, queryParams);
    
    // 2. Testar chamada da API
    console.log(`\n🌐 TESTANDO CHAMADA DA API...`);
    try {
      const apiResponse = await apiCall(queryParams);
      console.log(`✅ Resposta da API:`, apiResponse);
      
      if (apiResponse && apiResponse.length > 0) {
        console.log(`📊 ${apiResponse.length} ordens encontradas na API`);
      } else {
        console.warn(`⚠️ Nenhuma ordem encontrada na API para o período`);
      }
    } catch (error) {
      console.error(`❌ Erro na chamada da API:`, error);
    }
    
    // 3. Verificar dados locais
    console.log(`\n💾 VERIFICANDO DADOS LOCAIS...`);
    const { data: ordensLocais, error: localError } = await supabase
      .from('ordens_servico')
      .select('numero, empresa_nome, data_real')
      .gte('data_real', inicio)
      .lte('data_real', fim)
      .order('data_real', { ascending: false });

    if (localError) {
      console.error(`❌ Erro ao buscar dados locais:`, localError);
    } else {
      console.log(`📊 ${ordensLocais?.length || 0} ordens encontradas localmente`);
      if (ordensLocais && ordensLocais.length > 0) {
        console.log(`📋 Primeiras 5 ordens locais:`);
        ordensLocais.slice(0, 5).forEach(ordem => {
          console.log(`  - OS ${ordem.numero} (${ordem.empresa_nome}) - ${ordem.data_real}`);
        });
      }
    }
    
  } catch (error) {
    console.error(`❌ Erro no diagnóstico de sincronização:`, error);
  }
};

// Função para limpar cache e forçar nova busca
export const limparCache = () => {
  console.log('🧹 Limpando cache de valores...');
  
  try {
    // Limpar cache usando a função do módulo
    clearClienteCache();
    console.log('✅ Cache limpo com sucesso');
    console.log('🎯 Execute novamente os testes para ver os valores corretos.');
  } catch (error) {
    console.error('❌ Erro ao limpar cache:', error);
  }
};

// Função para verificar cliente específico
export const verificarCliente = async (nomeCliente: string) => {
  console.log(`🔍 === VERIFICANDO CLIENTE: ${nomeCliente} ===`);
  
  try {
    // Buscar cliente pelo nome
    const { data: cliente, error: clienteError } = await supabase
      .from('clientes_cadastro')
      .select('*')
      .eq('nome', nomeCliente)
      .maybeSingle();

    if (clienteError || !cliente) {
      console.error(`❌ Cliente "${nomeCliente}" não encontrado`);
      return;
    }

    console.log(`✅ Cliente encontrado: ${cliente.nome} (ID: ${cliente.id})`);
    
    // Buscar valores históricos
    const { data: valoresHistorico, error: valoresError } = await supabase
      .from('clientes_valores_historico')
      .select('*')
      .eq('cliente_id', cliente.id)
      .order('id', { ascending: false });

    if (valoresError) {
      console.error(`❌ Erro ao buscar valores históricos:`, valoresError);
      return;
    }

    console.log(`📊 Valores históricos do cliente ${cliente.nome}:`);
    if (valoresHistorico && valoresHistorico.length > 0) {
      valoresHistorico.forEach((valor, index) => {
        console.log(`  ${index + 1}. ID: ${valor.id}, Data: ${valor.data_reajuste}, Valor: R$ ${valor.valor_hora}`);
      });
      
      const valorMaisRecente = valoresHistorico[0];
      console.log(`\n💰 Valor mais recente: R$ ${valorMaisRecente.valor_hora} (ID: ${valorMaisRecente.id}, Data: ${valorMaisRecente.data_reajuste})`);
    } else {
      console.warn(`⚠️ Cliente ${cliente.nome} não possui valores históricos`);
    }

  } catch (error) {
    console.error(`❌ Erro ao verificar cliente:`, error);
  }
};

// Função para listar clientes sem valor/hora
export const listarClientesSemValor = async () => {
  console.log('📊 === LISTANDO CLIENTES SEM VALOR/HORA ===');
  
  try {
    const { data: clientes, error } = await supabase
      .from('clientes_cadastro')
      .select(`
        id,
        nome,
        valores_historico:clientes_valores_historico(*)
      `);

    if (error) {
      console.error('❌ Erro ao buscar clientes:', error);
      return;
    }

    const clientesSemValor = clientes?.filter(cliente => 
      !cliente.valores_historico || cliente.valores_historico.length === 0
    ) || [];

    console.log(`⚠️ ${clientesSemValor.length} clientes sem valor/hora:`);
    clientesSemValor.forEach(cliente => {
      console.log(`  - ${cliente.nome} (ID: ${cliente.id})`);
    });

  } catch (error) {
    console.error('❌ Erro ao listar clientes:', error);
  }
};

// Expor funções globalmente para uso no console
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const globalWindow = window as any;
  globalWindow.diagnosticarOS = diagnosticarOS;
  globalWindow.diagnosticarOSs = diagnosticarOSs;
  globalWindow.corrigirOS = corrigirOS;
  globalWindow.corrigirOSs = corrigirOSsProblematicas;
  globalWindow.verificarCliente = verificarCliente;
  globalWindow.listarClientesSemValor = listarClientesSemValor;
  globalWindow.diagnosticarSincronizacao = diagnosticarSincronizacao;
  globalWindow.limparCache = limparCache;
  
  console.log('🔧 Funções de diagnóstico disponíveis no console:');
  console.log('  - diagnosticarOS(numeroOS)');
  console.log('  - diagnosticarOSs(numerosOS)');
  console.log('  - corrigirOS(numeroOS)');
  console.log('  - corrigirOSs()');
  console.log('  - verificarCliente(nomeCliente)');
  console.log('  - listarClientesSemValor()');
  console.log('  - diagnosticarSincronizacao(dataInicio?, dataFim?)');
  console.log('  - limparCache()');
} 